import React, { useEffect, useRef } from 'react'
import { Box, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material'

import LoadingSpinner from '../../../../components/LoadingSpinner'
import { REFUND_APPROVAL_TEXT } from '../../../constants/refundApprovalText'
import { UI_COLOR_PRIMARY, UI_COLOR_TEXT_TERTIARY } from '../../../../constants/uiColors'
import RefundScanCameraView from './RefundScanCameraView'
import RefundScanEmptyProduct from './RefundScanEmptyProduct'
import RefundScanModalFooter from './RefundScanModalFooter'
import RefundScanPackagingChoiceDialog from './RefundScanPackagingChoiceDialog'
import RefundScanSearchControls from './RefundScanSearchControls'
import RefundScanSelectedLinePanel from './RefundScanSelectedLinePanel'
import useRefundScanModal from '../../hooks/useRefundScanModal'

const RefundScanModal = ({ open, onClose, lines = [], onSave, initialLineUid = '' }) => {
  const searchInputRef = useRef(null)
  const {
    search,
    verifiedQuantity,
    manualError,
    cameraOpen,
    cameraStatus,
    cameraError,
    cameraCodeError,
    canSwitchCamera,
    videoRef,
    canvasRef,
    selectedLine,
    selectedLineStatus,
    selectedOriginalQuantity,
    selectedUnit,
    selectedVerifiedQuantity,
    hasSearch,
    isEditMode,
    allowsDecimals,
    quantityIsValid,
    quantityError,
    isUpdatingLine,
    canSave,
    showManualSearchControls,
    showCameraControls,
    showSaveLoading,
    processedNoticeLabel,
    saveLoadingText,
    saveButtonLabel,
    modalPaperSize,
    openCamera,
    refocusCamera,
    switchCamera,
    stopCamera,
    startNewScan,
    packagingChoices,
    handleSelectPackaging,
    closePackagingChoice,
    handleSearchLine,
    handleSearchChange,
    handleVerifiedQuantityChange,
    handleClose,
    handleSave
  } = useRefundScanModal({ open, onClose, lines, onSave, initialLineUid })

  useEffect(() => {
    if (!open || cameraOpen || showSaveLoading || !showManualSearchControls) return

    const focusTimer = window.setTimeout(() => {
      searchInputRef.current?.focus()
      searchInputRef.current?.select?.()
    }, 0)

    return () => window.clearTimeout(focusTimer)
  }, [open, cameraOpen, showSaveLoading, showManualSearchControls])

  return (
    <Dialog
      open={open}
      onClose={showSaveLoading ? undefined : handleClose}
      fullWidth
      maxWidth={false}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'center',
          py: { xs: 2, sm: 3 }
        }
      }}
      PaperProps={{
        sx: {
          width: { ...modalPaperSize.width, xs: 'calc(100vw - 24px)' },
          height: modalPaperSize.height,
          maxWidth: { xs: 'calc(100vw - 24px)', sm: 'calc(100vw - 32px)' },
          maxHeight: { xs: 'calc(100dvh - 24px)', sm: 'calc(100vh - 24px)' },
          m: 0,
          borderRadius: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          transform: { xs: 'none', sm: 'translateY(-5vh)' }
        }
      }}
    >
      <DialogTitle sx={{ px: 3, pt: 2.25, pb: 0 }}>
        <Typography sx={{ fontSize: isEditMode ? 24 : 20, fontWeight: 500, lineHeight: 1.2 }}>
          {isEditMode ? REFUND_APPROVAL_TEXT.editLineTitle : REFUND_APPROVAL_TEXT.scanModalTitle}
        </Typography>
        <Typography
          sx={{
            fontSize: isEditMode ? 14 : 11,
            color: UI_COLOR_TEXT_TERTIARY,
            lineHeight: 2,
            mb: isEditMode ? 1.5 : 3
          }}
        >
          {isEditMode
            ? REFUND_APPROVAL_TEXT.editLineSubtitle
            : REFUND_APPROVAL_TEXT.scanModalSubtitle}
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          pt: isEditMode ? 1 : 2,
          pb: cameraOpen || showSaveLoading ? 2.5 : 0,
          flex: 1,
          overflowY: cameraOpen ? 'hidden' : 'auto'
        }}
      >
        {showSaveLoading ? (
          <Box
            sx={{
              height: '100%',
              minHeight: 260,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <LoadingSpinner text={saveLoadingText} color={UI_COLOR_PRIMARY} />
          </Box>
        ) : cameraOpen ? (
          <RefundScanCameraView
            videoRef={videoRef}
            canvasRef={canvasRef}
            cameraError={cameraError}
            cameraStatus={cameraStatus}
            cameraCodeError={cameraCodeError}
            canSwitchCamera={canSwitchCamera}
            onRefocus={refocusCamera}
            onSwitchCamera={switchCamera}
            onBack={() => stopCamera()}
          />
        ) : (
          <>
            {showManualSearchControls && (
              <RefundScanSearchControls
                isEditMode={isEditMode}
                showCameraControls={showCameraControls}
                search={search}
                manualError={manualError}
                hasSearch={hasSearch}
                hasSelectedLine={Boolean(selectedLine)}
                autoFocus
                inputRef={searchInputRef}
                onOpenCamera={openCamera}
                onSearchChange={handleSearchChange}
                onSearchLine={handleSearchLine}
              />
            )}

            {selectedLine ? (
              <RefundScanSelectedLinePanel
                selectedLine={selectedLine}
                selectedLineStatus={selectedLineStatus}
                selectedOriginalQuantity={selectedOriginalQuantity}
                selectedUnit={selectedUnit}
                selectedVerifiedQuantity={selectedVerifiedQuantity}
                verifiedQuantity={verifiedQuantity}
                allowsDecimals={allowsDecimals}
                quantityError={quantityError}
                quantityIsValid={quantityIsValid}
                isEditMode={isEditMode}
                isUpdatingLine={isUpdatingLine}
                processedNoticeLabel={processedNoticeLabel}
                onStartNewScan={startNewScan}
                onVerifiedQuantityChange={handleVerifiedQuantityChange}
              />
            ) : (
              <RefundScanEmptyProduct manualError={manualError} />
            )}
          </>
        )}
      </DialogContent>

      {!cameraOpen && !showSaveLoading && (
        <RefundScanModalFooter
          canSave={canSave}
          saveButtonLabel={saveButtonLabel}
          onClose={handleClose}
          onSave={handleSave}
        />
      )}

      <RefundScanPackagingChoiceDialog
        open={packagingChoices.length > 1}
        lines={packagingChoices}
        onSelect={handleSelectPackaging}
        onClose={closePackagingChoice}
      />
    </Dialog>
  )
}

export default RefundScanModal
